import {useIntl} from 'react-intl';
import React, {useMemo, useState} from 'react';
import {DeploymentStatusType} from '../../../types/deployment/DeploymentResponse';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import WarningIcon from '@material-ui/icons/Warning';
import {IconButton} from '@material-ui/core';
import StopCicleIcon from '../../../assets/icons/StopCircle';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import './DeploymentActionButton.scss';
import {InputSource} from '../../../types/deployment/InferenceInputSource';
import {InputChoice} from '../DeploymentDetails/DeploymentDetails';
import {StudioTooltip} from '../../../base-components/StudioTooltip';

type DeploymentActionButtonProps = {
  status: DeploymentStatusType;
  formStatusOverride?: boolean;
  inputSourceLocationType?: InputChoice | null;
  inputSourceId?: string | null;
  inputSourceType?: InputSource;
  streamingUrl?: string | null;
  confirmTitle?: string;
  confirmMessage?: string;
  confirmSubmitLabel?: string;
  detailedTooltip?: boolean;
  onStart?: () => void;
  onStop?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  permanentDeployment?: boolean;
};

export const DeploymentActionButton = ({
  status,
  formStatusOverride,
  inputSourceLocationType,
  inputSourceId,
  inputSourceType,
  streamingUrl,
  onStart,
  onStop,
  onOpen,
  onClose,
  confirmTitle,
  confirmMessage,
  confirmSubmitLabel,
  detailedTooltip,
  permanentDeployment,
}: DeploymentActionButtonProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const intl = useIntl();
  const handleClick = () => {
    if (status === 'IDLE') {
      onStart && onStart();
    } else if (status !== 'ERROR') {
      setIsConfirmOpen(true);
      onOpen && onOpen();
    }
  };

  const icon =
    status === 'ERROR' ? (
      <WarningIcon
        id="deploy-warning-icon"
        className="deploy-action-button__icon--warning"
        fontSize="medium"
      />
    ) : status === 'IDLE' ? (
      <PlayCircleFilledIcon fontSize="medium" />
    ) : (
      <StopCicleIcon />
    );

  const areDeploymentSourcesValid = () => {
    if (inputSourceLocationType === 'DEVICE' && inputSourceId) {
      return true;
    } else if (inputSourceLocationType === 'EXTERNAL' && inputSourceType) {
      if (inputSourceId) {
        return true;
      } else if (streamingUrl) {
        return true;
      }
    }
    if (formStatusOverride) {
      return true;
    } else {
      return false;
    }
  };

  const disableButtonWithTooltip = useMemo(
    () =>
      (status === 'IDLE' && !areDeploymentSourcesValid()) ||
      ((status === 'RUNNING' || status === 'STARTING') && !permanentDeployment),
    [status, permanentDeployment, areDeploymentSourcesValid]
  );

  let tooltip =
    (status === 'RUNNING' || status === 'STARTING') && !permanentDeployment
      ? 'inferenceApp.deviceInUseInSPAorGSP'
      : detailedTooltip
      ? 'prodDeployment.detection.invalid.sources.tooltip'
      : 'prodDeployment.detection.first.start.tooltip';
  tooltip = intl.formatMessage({id: tooltip});

  return (
    <>
      {disableButtonWithTooltip ? (
        <StudioTooltip
          title={disableButtonWithTooltip ? tooltip : undefined}
          placement="top"
        >
          <IconButton className="deploy-action-button__disabled">{icon}</IconButton>
        </StudioTooltip>
      ) : (
        <IconButton
          className="deploy-action-button"
          disabled={
            ['ERROR', 'STARTING', 'STOPPING', 'DEPLOYING', 'DELETED'].includes(status) ||
            disableButtonWithTooltip
          }
          type="submit"
          form="deployment-details-form"
          onClick={handleClick}
          data-testid={
            status === 'ERROR'
              ? 'deploy-warning-icon'
              : status === 'IDLE'
              ? 'deploy-start-icon'
              : 'deploy-stop-icon'
          }
        >
          {icon}
        </IconButton>
      )}
      <ConfirmDialog
        type="confirm"
        title={confirmTitle ?? intl.formatMessage({id: 'prodDeployment.confirmTitle'})}
        message={
          confirmMessage ?? intl.formatMessage({id: 'prodDeployment.confirmMessage'})
        }
        submitLabel={
          confirmSubmitLabel ??
          intl.formatMessage({id: 'prodDeployment.confirmSubmitLabel'})
        }
        open={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          onClose && onClose();
        }}
        onOk={() => {
          setIsConfirmOpen(false);
          onClose && onClose();
          onStop && onStop();
        }}
      />
    </>
  );
};
